"use strict";

Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.default = void 0;

var _react = _interopRequireDefault(require("react"));

var _propTypes = _interopRequireDefault(require("prop-types"));

var _reactDisplace = _interopRequireDefault(require("react-displace"));

var _debounce = _interopRequireDefault(require("debounce"));

var _calculatePosition = _interopRequireDefault(require("./calculate-position"));

var _getScrollableParents = _interopRequireDefault(require("./get-scrollable-parents"));

var _createScrollListener = _interopRequireDefault(require("./create-scroll-listener"));

var _getWindow = _interopRequireDefault(require("../utils/get-window"));

function _interopRequireDefault(obj) { return obj && obj.__esModule ? obj : { default: obj }; }

function _defineProperty(obj, key, value) { if (key in obj) { Object.defineProperty(obj, key, { value: value, enumerable: true, configurable: true, writable: true }); } else { obj[key] = value; } return obj; }

let popoverPositionerList = [];
/**
 * Secretly mounts a popover and its pointer, calculates their positions, then repositions and exposes them.
 *
 * @param {ReactNode} children
 * @param {Function} getAnchorElement - Function that returns the popover's anchor element.
 * @param {'top' | 'bottom' | 'left' | 'right' } [placement] - See options for calculatePosition.
 * @param { 'top' | 'bottom' | 'left' | 'right' | 'center' } [alignment] - See options for calculatePosition.
 * @param {boolean} [hasPointer] - See options for calculatePosition.
 * @param {number} [pointerBase] - See options for calculatePosition.
 * @param {number} [pointerAltitude] - See options for calculatePosition.
 * @param {string} [pointerColor] - See options for calculatePosition.
 * @param {number} [offsetFromAnchor] - See options for calculatePosition.
 * @param {boolean} [hideWhenAnchorIsOffscreen] - See options for calculatePosition.
 * @param {boolean} [allowPlacementAxisChange] - See options for calculatePosition.
 * @param {boolean} [containWithinViewport] - See options for calculatePosition.
 * @param {Function} [getContainingElement] - See options for calculatePosition.
 * @param {Function} [onElement] - A function that receives the containing element.
 * @param {number} [zIndex] - z-index for body element.
 */

class PopoverPositioner extends _react.default.PureComponent {
  constructor(...args) {
    super(...args);

    _defineProperty(this, "getScrollableParentElement", () => {
      return this.scrollableParents[0];
    });

    _defineProperty(this, "setPosition", () => {
      const {
        props
      } = this;
      const positionData = this.props.calculatePosition({
        getScrollableParentElement: this.getScrollableParentElement,
        getPopoverElement: this.getPopoverElement,
        getAnchorElement: props.getAnchorElement,
        placement: props.placement,
        alignment: props.alignment,
        hasPointer: props.hasPointer,
        offsetFromAnchor: props.offsetFromAnchor,
        pointerBase: props.pointerBase,
        pointerAltitude: props.pointerAltitude,
        pointerColor: props.pointerColor,
        hideWhenAnchorIsOffscreen: props.hideWhenAnchorIsOffscreen,
        allowPlacementAxisChange: props.allowPlacementAxisChange,
        containWithinViewport: props.containWithinViewport,
        getContainingElement: props.getContainingElement
      });
      const bodyElement = this.bodyElement;

      if (bodyElement) {
        bodyElement.style.setProperty('visibility', 'visible');

        for (let key in positionData.bodyPositioningStyle) {
          bodyElement.style.setProperty(key, positionData.bodyPositioningStyle[key]);
        }
      }

      const pointerElement = this.pointerElement;

      if (pointerElement) {
        pointerElement.style.setProperty('visibility', 'visible');

        for (let key in positionData.pointerPositioningStyle) {
          pointerElement.style.setProperty(key, positionData.pointerPositioningStyle[key]);
        }
      }
    });

    _defineProperty(this, "setBodyElement", element => {
      this.bodyElement = element;
    });

    _defineProperty(this, "setContainerElement", element => {
      this.containerElement = element;
      if (this.props.onElement) this.props.onElement(element);
    });

    _defineProperty(this, "getPopoverElement", () => {
      return this.bodyElement;
    });

    _defineProperty(this, "setPointerElement", element => {
      this.pointerElement = element;
    });
  }

  componentDidMount() {
    popoverPositionerList.push(this.setPosition);
    this.scrollableParents = this.props.getScrollableParents(this.props.getAnchorElement());
    this.handleResize = (0, _debounce.default)(this.setPosition, 200);
    this.props.getWindow().addEventListener('resize', this.handleResize);
    this.scrollListeners = this.scrollableParents.map(scrollableParent => {
      const scrollListener = this.props.createScrollListener(scrollableParent, this.setPosition);
      scrollListener.start();
      return scrollListener;
    }, this);
    this.setPosition();
  }

  componentDidUpdate() {
    this.setPosition();
  }

  componentWillUnmount() {
    this.props.getWindow().removeEventListener('resize', this.handleResize);
    this.scrollListeners.forEach(scrollListener => {
      scrollListener.stop();
    });
    popoverPositionerList.splice(popoverPositionerList.indexOf(this.setPosition), 1);
  }

  render() {
    const basicStyle = {
      position: 'absolute',
      zIndex: this.props.zIndex,
      top: 0,
      left: 0,
      visibility: 'hidden'
    };
    return /*#__PURE__*/_react.default.createElement("div", {
      ref: this.setContainerElement
    }, /*#__PURE__*/_react.default.createElement("div", {
      "data-test": "popover-body",
      ref: this.setBodyElement,
      style: basicStyle
    }, this.props.children), /*#__PURE__*/_react.default.createElement("div", {
      "data-test": "popover-pointer",
      ref: this.setPointerElement,
      style: basicStyle
    }));
  }

}

_defineProperty(PopoverPositioner, "propTypes", {
  children: _propTypes.default.node.isRequired,
  getAnchorElement: _propTypes.default.func.isRequired,
  placement: _propTypes.default.oneOf(['top', 'bottom', 'left', 'right']),
  alignment: _propTypes.default.oneOf(['top', 'bottom', 'left', 'right', 'center']),
  pointerBase: _propTypes.default.number,
  hasPointer: _propTypes.default.bool,
  pointerAltitude: _propTypes.default.number,
  pointerColor: _propTypes.default.string,
  offsetFromAnchor: _propTypes.default.number,
  hideWhenAnchorIsOffscreen: _propTypes.default.bool,
  allowPlacementAxisChange: _propTypes.default.bool,
  containWithinViewport: _propTypes.default.bool,
  getContainingElement: _propTypes.default.func,
  onElement: _propTypes.default.func,
  zIndex: _propTypes.default.number,
  // For mockery
  calculatePosition: _propTypes.default.func,
  getScrollableParents: _propTypes.default.func,
  createScrollListener: _propTypes.default.func,
  getWindow: _propTypes.default.func
});

_defineProperty(PopoverPositioner, "defaultProps", {
  calculatePosition: _calculatePosition.default,
  getScrollableParents: _getScrollableParents.default,
  createScrollListener: _createScrollListener.default,
  getWindow: _getWindow.default
});

const displaced = (0, _reactDisplace.default)(PopoverPositioner);

displaced.recalculatePositions = () => {
  popoverPositionerList.forEach(recalcFn => {
    recalcFn();
  });
};

var _default = displaced;
exports.default = _default;